/**
 * Générateur — Automatisme : connaître la notion de médiatrice, de cercle
 * circonscrit.
 * Attendu ID : 1078 | Niveau : 5ème | Domaine : Triangles
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Est-ce la médiatrice ?          (QCM sur figure codée + texte à trous : la définition
 *     + V/F sur la médiatrice avec une ligne numérique recalculée — carte fusionnée, décision prof 2026-09-03)
 *   - Un point sur la médiatrice      (saisie entière, QCM justification — le QCM nature
 *     « MAB isocèle en M » retiré, décision prof 2026-09-03)
 *   - Le cercle circonscrit           (QCM « que représente O ? », saisie rayon, texte à trous :
 *     définition, centre, et le pourquoi de la question d. — remplace le QCM 3e médiatrice, décision prof 2026-09-03 ;
 *     + V/F sur le cercle circonscrit, lignes déplacées depuis le V/F médiatrice, décision prof 2026-09-03)
 *
 * ── Plan de cartes validé (2026-09-02) ─────────────────────────────────────
 * Source : Automatisme n°40 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : médiatrice et cercle circonscrit INTRODUITS en 6e (définition, propriété
 * caractéristique, concours, construction), basculés en automatisme en 5e ; le
 * CM2 fournit perpendicularité, milieu, cercle/rayon/diamètre.
 * P2 : deux sens installés — la DÉFINITION (perpendiculaire au milieu) et la
 * PROPRIÉTÉ CARACTÉRISTIQUE (équidistance des extrémités, dans les deux sens).
 * Distracteurs sur les confusions : perpendiculaire sans milieu, milieu sans
 * angle droit, équidistance des CÔTÉS au lieu des sommets, hauteurs, « milieu
 * du triangle », centre « toujours à l'intérieur ».
 * P3 : longueurs entières en cm ; IA = IB exige AB pair. P6/P10 : on ne mesure
 * rien à l'écran, la mesure n'est jamais une justification recevable.
 * P7 : pas de Pythagore, pas de cercle inscrit nommé (4e) ; une seule étape.
 * P8 : exigibles depuis la 6e : médiatrice, milieu, perpendiculaire,
 * équidistant, cercle circonscrit, concourantes, [AB], (d).
 * P11 : « médiatrice » et « équidistance » sont deux façons de dire la même
 * chose (fiche) — la réciproque est travaillée explicitement (justification).
 * P12 : rémanence forte 6e → 5e, pas d'item de réactivation.
 * INVARIANTS :
 *   - le DESSIN ne contredit jamais le codage (throw) ; allure trompeuse
 *     autorisée (perpendiculaire dessinée mais non codée) à 35 % ;
 *   - les figures embarquent data-figure (6 décimales) et data-code
 *     (carre / tick) pour les solveurs ; les réponses de saisie sont ENTIÈRES ;
 *   - le cercle circonscrit est CALCULÉ (centre = intersection des
 *     médiatrices), triangle obtusangle à 25 % pour que O sorte du triangle ;
 *   - V/F : deux pools de 10 lignes (5 V / 5 F), médiatrice et cercle, fenêtre
 *     stricte des 4 dernières servies, ligne numérique jamais déductible d'une ligne fixe ;
 *   - pas de calculatrice.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1078;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_LECTURE = 'Est-ce la médiatrice ?';
const L_POINT = 'Un point sur la médiatrice';
const L_CERCLE = 'Le cercle circonscrit';

// ─── Géométrie élémentaire ──────────────────────────────────────────────────

const vec = (P, Q) => [Q[0] - P[0], Q[1] - P[1]];
const dot = (u, v) => u[0] * v[0] + u[1] * v[1];
const norme = (u) => Math.hypot(u[0], u[1]);
const milieu = (P, Q) => [(P[0] + Q[0]) / 2, (P[1] + Q[1]) / 2];
const tourner = (pts, deg) => {
    const a = (deg * Math.PI) / 180; const co = Math.cos(a); const si = Math.sin(a);
    return pts.map(([x, y]) => [x * co - y * si, x * si + y * co]);
};
const unitaire = (u) => { const L = norme(u) || 1; return [u[0] / L, u[1] / L]; };
const perp = (u) => [-u[1], u[0]];

/** Centre du cercle circonscrit (intersection de deux médiatrices). */
const circonscrit = (A, B, C) => {
    const d = 2 * (A[0] * (B[1] - C[1]) + B[0] * (C[1] - A[1]) + C[0] * (A[1] - B[1]));
    if (Math.abs(d) < 1e-9) throw new Error('gen_1078 : points alignés');
    const a2 = dot(A, A); const b2 = dot(B, B); const c2 = dot(C, C);
    const ux = (a2 * (B[1] - C[1]) + b2 * (C[1] - A[1]) + c2 * (A[1] - B[1])) / d;
    const uy = (a2 * (C[0] - B[0]) + b2 * (A[0] - C[0]) + c2 * (B[0] - A[0])) / d;
    return [ux, uy];
};

// ─── Rendu SVG générique ────────────────────────────────────────────────────
// Une scène = liste de primitives en coordonnées abstraites ; le cadre est
// ajusté sur l'ensemble des points d'appui (bbox), puis chaque primitive est
// dessinée. Les codages portent data-code pour les solveurs.

const W = 320; const H = 240; const MARGE = 30;

const cadreDe = (appuis) => {
    const xs = appuis.map((p) => p[0]); const ys = appuis.map((p) => p[1]);
    const xmin = Math.min(...xs); const xmax = Math.max(...xs);
    const ymin = Math.min(...ys); const ymax = Math.max(...ys);
    const ech = Math.min((W - 2 * MARGE) / Math.max(xmax - xmin, 1e-6), (H - 2 * MARGE) / Math.max(ymax - ymin, 1e-6));
    const X = (x) => MARGE + (x - xmin) * ech + ((W - 2 * MARGE) - (xmax - xmin) * ech) / 2;
    const Y = (y) => MARGE + (ymax - y) * ech + ((H - 2 * MARGE) - (ymax - ymin) * ech) / 2;
    return { X, Y, ech, P: (p) => [X(p[0]), Y(p[1])] };
};

const f1 = (v) => v.toFixed(1);

const svgSegment = (P, Q, attrs = 'stroke="#1E3A8A" stroke-width="2.2"') =>
    `<line x1="${f1(P[0])}" y1="${f1(P[1])}" x2="${f1(Q[0])}" y2="${f1(Q[1])}" ${attrs}/>`;

/** Trait(s) d'égalité au milieu de [PQ] (en px). */
const svgTicks = (P, Q, n, cote) => {
    const m = milieu(P, Q); const u = unitaire(vec(P, Q)); const nv = perp(u);
    let s = '';
    for (let t = 0; t < n; t++) {
        const off = (t - (n - 1) / 2) * 5;
        const c = [m[0] + u[0] * off, m[1] + u[1] * off];
        s += `<line x1="${f1(c[0] - nv[0] * 6)}" y1="${f1(c[1] - nv[1] * 6)}" x2="${f1(c[0] + nv[0] * 6)}" y2="${f1(c[1] + nv[1] * 6)}" stroke="#111827" stroke-width="2" data-code="tick" data-cote="${cote}" data-n="${n}"/>`;
    }
    return s;
};

/** Petit carré d'angle droit en S, entre les directions u1 et u2 (px). */
const svgCarre = (S, u1, u2, nom) => {
    const k = 11; const a = unitaire(u1); const b = unitaire(u2);
    return `<polyline points="${f1(S[0] + a[0] * k)},${f1(S[1] + a[1] * k)} ${f1(S[0] + (a[0] + b[0]) * k)},${f1(S[1] + (a[1] + b[1]) * k)} ${f1(S[0] + b[0] * k)},${f1(S[1] + b[1] * k)}" fill="none" stroke="#111827" stroke-width="1.8" data-code="carre" data-sommet="${nom}"/>`;
};

const svgPoint = (P, nom, dir, dist = 15) => {
    const d = unitaire(dir);
    return `<circle cx="${f1(P[0])}" cy="${f1(P[1])}" r="2.4" fill="#1e293b"/>`
        + `<text x="${f1(P[0] + d[0] * dist)}" y="${f1(P[1] + d[1] * dist + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${nom}</text>`;
};

/** Recul de l'étiquette d'un point placé entre deux droites : à 10 px au moins de chacune. */
const distanceEtiquette = (u1, u2) => {
    const theta = Math.acos(Math.max(-1, Math.min(1, dot(unitaire(u1), unitaire(u2)))));
    return Math.max(15, 10 / Math.sin(theta / 2));
};

const svgTexteLibre = (P, txt, fill = '#B45309') =>
    `<text x="${f1(P[0])}" y="${f1(P[1])}" font-size="12.5" font-weight="700" fill="${fill}" text-anchor="middle">${txt}</text>`;

const dataFigure = (points) => Object.entries(points).map(([n, p]) => `${n}:${p[0].toFixed(6)},${p[1].toFixed(6)}`).join('|');

const svgEnvelopper = (contenu, points, extra = '') =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-figure="${dataFigure(points)}"${extra}>${contenu}</svg>`;

// ─── Carte 1a : est-ce la médiatrice ? (QCM sur figure codée) ──────────────

const ETATS_LECTURE = ['les_deux', 'perp_seule', 'milieu_seul', 'aucun'];

const OPTIONS_LECTURE = {
    les_deux: 'Oui : (d) est perpendiculaire à [AB] et passe par son milieu.',
    milieu_seul: 'Non : le milieu est codé, mais pas l\'angle droit.',
    perp_seule: 'Non : l\'angle droit est codé, mais pas le milieu.',
    aucun: 'Non : ni l\'angle droit ni le milieu ne sont codés.',
};

/** Figure : segment [AB], point I sur [AB], droite (d) passant par I. */
const construireFigureLecture = (etat) => {
    const trompeuse = Math.random() < 0.35;
    const perpCodee = etat === 'les_deux' || etat === 'perp_seule';
    const milieuCode = etat === 'les_deux' || etat === 'milieu_seul';
    // Dessin : ce qui est codé est dessiné vrai ; ce qui n'est pas codé est
    // dessiné faux, sauf allure trompeuse (dessiné vrai mais non codé).
    const perpDessinee = perpCodee || trompeuse;
    const milieuDessine = milieuCode || trompeuse;
    const t = milieuDessine ? 0.5 : randomChoice([0.33, 0.38, 0.62, 0.67]);
    const angle = perpDessinee ? 90 : randomChoice([62, 68, 112, 118]);
    const A = [0, 0]; const B = [6, 0]; const I = [6 * t, 0];
    const rad = (angle * Math.PI) / 180; const u = [Math.cos(rad), Math.sin(rad)];
    const D1 = [I[0] + u[0] * 3, I[1] + u[1] * 3]; const D2 = [I[0] - u[0] * 3, I[1] - u[1] * 3];
    const rot = Math.random() < 0.3 ? 0 : randInt(5, 355);
    const [rA, rB, rI, rD1, rD2] = tourner([A, B, I, D1, D2], rot);
    // Gardes : le codage ne contredit jamais le dessin.
    if (perpCodee && Math.abs(dot(vec(rA, rB), vec(rD1, rD2))) > 1e-6) throw new Error('gen_1078 : angle droit codé mais non dessiné');
    if (milieuCode && Math.abs(norme(vec(rA, rI)) - norme(vec(rI, rB))) > 1e-6) throw new Error('gen_1078 : milieu codé mais non dessiné');
    return { A: rA, B: rB, I: rI, D1: rD1, D2: rD2, perpCodee, milieuCode, trompeuse, rot };
};

const svgFigureLecture = (fig) => {
    const { A, B, I, D1, D2 } = fig;
    const c = cadreDe([A, B, D1, D2]);
    const pA = c.P(A); const pB = c.P(B); const pI = c.P(I); const p1 = c.P(D1); const p2 = c.P(D2);
    let s = svgSegment(p1, p2, 'stroke="#B45309" stroke-width="2"');
    s += svgSegment(pA, pB);
    if (fig.milieuCode) s += svgTicks(pA, pI, 1, 'AI') + svgTicks(pI, pB, 1, 'IB');
    if (fig.perpCodee) s += svgCarre(pI, vec(pI, pB), vec(pI, p1), 'I');
    // I est nommé dans le quadrant OPPOSÉ au petit carré : à 45° des deux droites,
    // jamais sur [AB] ni sur (d) (retour prof 2026-09-02).
    const uIB = unitaire(vec(pI, pB)); const uID = unitaire(vec(pI, p1));
    s += svgPoint(pA, 'A', vec(pB, pA)) + svgPoint(pB, 'B', vec(pA, pB));
    s += svgPoint(pI, 'I', [-(uIB[0] + uID[0]), -(uIB[1] + uID[1])], distanceEtiquette(uIB, uID));
    s += `<text x="${f1(p1[0] + (p1[0] - pI[0]) * 0.12)}" y="${f1(p1[1] + (p1[1] - pI[1]) * 0.12 + 5)}" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle">(d)</text>`;
    return svgEnvelopper(s, { A, B, I, D1, D2 });
};

let derniereSignatureLecture1078 = null;

const genLectureQcm = () => {
    let payload = construireLectureQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureLecture1078; t++) payload = construireLectureQcm();
    derniereSignatureLecture1078 = payload._meta.params.signature;
    return payload;
};

const construireLectureQcm = () => {
    const etat = randomChoice(ETATS_LECTURE);
    const fig = construireFigureLecture(etat);
    const correct = OPTIONS_LECTURE[etat];
    const autres = ETATS_LECTURE.filter((e) => e !== etat).map((e) => OPTIONS_LECTURE[e]);
    const texte = '<p>Le point I est sur le segment [AB] et la droite (d) passe par I.</p><p>D\'après le codage, (d) est-elle la médiatrice de [AB] ?</p>';
    const payload = makeQcm([correct, ...autres], correct, texte, {
        ...opts(L_LECTURE), elements_additionnels: imgElement(svgFigureLecture(fig), 'medium'),
    });
    payload._meta.params = { etat, trompeuse: fig.trompeuse, signature: `${etat}-${fig.trompeuse}-${fig.rot}` };
    return payload;
};

// ─── Carte 1b : la définition (texte à trous) ──────────────────────────────

const PHRASES_DEFINITION = [
    {
        phrase: 'La médiatrice d\'un segment est la droite {{perpendiculaire}} à ce segment et passant par son {{milieu}}.',
        distracteurs: ['parallèle', 'extrémité', 'sommet'],
    },
    {
        phrase: 'La médiatrice d\'un segment passe par le {{milieu}} de ce segment et lui est {{perpendiculaire}}.',
        distracteurs: ['centre', 'parallèle', 'extrémité'],
    },
    {
        phrase: 'Tout point de la médiatrice d\'un segment est à égale {{distance}} des deux {{extrémités}} de ce segment.',
        distracteurs: ['longueur', 'milieux', 'côtés'],
    },
    {
        phrase: 'Un point à égale distance des deux extrémités d\'un segment est sur la {{médiatrice}} de ce segment.',
        distracteurs: ['droite', 'parallèle', 'diagonale'],   // « hauteur » retiré : vocabulaire de 5e (2026-09-03)
    },
    {
        phrase: 'La médiatrice de [AB] est la {{droite}} perpendiculaire à [AB] qui passe par le {{milieu}} de [AB].',
        distracteurs: ['demi-droite', 'extrémité', 'centre'],
    },
    {
        phrase: 'Si M est sur la médiatrice de [AB] et en dehors de [AB], alors le triangle MAB est {{isocèle}} en {{M}}.',
        distracteurs: ['rectangle', 'A', 'équilatéral'],
    },
    // Les deux phrases sur le cercle circonscrit sont parties dans le texte à trous
    // de la carte « Le cercle circonscrit » (revue 2026-09-03, A5) ; remplacées par
    // la médiatrice comme axe de symétrie (symétrie axiale de 6e).
    {
        phrase: 'La médiatrice de [AB] est l\'axe de {{symétrie}} du segment [AB].',
        distracteurs: ['milieu', 'longueur', 'rotation'],
    },
    {
        phrase: 'Les points A et B sont {{symétriques}} par rapport à la médiatrice de [AB].',
        distracteurs: ['alignés', 'confondus', 'parallèles'],
    },
];
// Garde : aucun distracteur ne doit pouvoir remplacer un mot attendu sans faux.
PHRASES_DEFINITION.forEach((p) => {
    const attendus = [...p.phrase.matchAll(/\{\{([^}]+)\}\}/g)].map((m) => m[1]);
    if (attendus.length < 1 || attendus.length > 2) throw new Error('gen_1078 : une phrase à trous doit avoir un ou deux trous');
    if (p.distracteurs.some((d) => attendus.includes(d))) throw new Error('gen_1078 : distracteur égal à un mot attendu');
});

let derniereDefinition1078 = -1;

const genDefinitionTrou = () => {
    let k = randInt(0, PHRASES_DEFINITION.length - 1);
    if (k === derniereDefinition1078) k = (k + 1) % PHRASES_DEFINITION.length;
    derniereDefinition1078 = k;
    const { phrase, distracteurs } = PHRASES_DEFINITION[k];
    const payload = makeTexteTrou(phrase, '<p>Complète la phrase avec les mots proposés.</p>', {
        ...opts(L_LECTURE), mode: 'drag', distracteurs: shuffle(distracteurs), strict: false,
    });
    payload._meta.params = { phrase: k };
    return payload;
};

// ─── Carte 2 : un point sur la médiatrice ──────────────────────────────────

/** Figure : [AB], sa médiatrice (d) codée, M sur (d) hors de [AB]. */
const construireFigurePoint = (avecM) => {
    const A = [0, 0]; const B = [6, 0]; const I = [3, 0];
    const h = randomChoice([2.4, 2.8, 3.2]) * (Math.random() < 0.5 ? 1 : -1);
    const M = [3, h];
    // Sans M, la droite (d) est plus courte : le segment [AB] occupe le cadre.
    const ext = avecM ? Math.abs(h) + 1.2 : 2.0;
    const D1 = [3, ext]; const D2 = [3, -ext];
    const rot = Math.random() < 0.3 ? 0 : randInt(5, 355);
    const [rA, rB, rI, rM, rD1, rD2] = tourner([A, B, I, M, D1, D2], rot);
    return { A: rA, B: rB, I: rI, M: rM, D1: rD1, D2: rD2, avecM, rot };
};

const svgFigurePoint = (fig, etiquettes = {}) => {
    const { A, B, I, M, D1, D2 } = fig;
    const c = cadreDe([A, B, D1, D2, M]);
    const pA = c.P(A); const pB = c.P(B); const pI = c.P(I); const pM = c.P(M); const p1 = c.P(D1); const p2 = c.P(D2);
    let s = svgSegment(p1, p2, 'stroke="#B45309" stroke-width="2"');
    s += svgSegment(pA, pB);
    s += svgTicks(pA, pI, 1, 'AI') + svgTicks(pI, pB, 1, 'IB');
    s += svgCarre(pI, vec(pI, pB), vec(pI, p1), 'I');
    if (fig.avecM) {
        s += svgSegment(pM, pA, 'stroke="#1E3A8A" stroke-width="1.6" stroke-dasharray="5 4"');
        s += svgSegment(pM, pB, 'stroke="#1E3A8A" stroke-width="1.6" stroke-dasharray="5 4"');
    }
    const nAB = perp(unitaire(vec(pA, pB)));
    const versM = unitaire(vec(pI, pM));
    const uIB = unitaire(vec(pI, pB)); const uID = unitaire(vec(pI, p1));
    s += svgPoint(pA, 'A', vec(pB, pA)) + svgPoint(pB, 'B', vec(pA, pB));
    s += svgPoint(pI, 'I', [-(uIB[0] + uID[0]), -(uIB[1] + uID[1])], distanceEtiquette(uIB, uID));
    if (fig.avecM) s += svgPoint(pM, 'M', perp(versM));
    [['MA', pA], ['MB', pB]].forEach(([cle, pX]) => {
        if (!etiquettes[cle]) return;
        const m = milieu(pM, pX); const n = perp(unitaire(vec(pM, pX)));
        const cote = dot(n, vec(pI, m)) > 0 ? 1 : -1;
        s += svgTexteLibre([m[0] + n[0] * 14 * cote, m[1] + n[1] * 14 * cote + 4], etiquettes[cle]);
    });
    [['IA', pA], ['IB', pB]].forEach(([cle, pX]) => {
        if (!etiquettes[cle]) return;
        // aux 3/5 de [IX] depuis I, du côté de (d) opposé au petit carré — assez loin de « I »
        const m = [pI[0] + (pX[0] - pI[0]) * 0.6, pI[1] + (pX[1] - pI[1]) * 0.6];
        const cote = dot(nAB, uID) > 0 ? -1 : 1;
        s += svgTexteLibre([m[0] + nAB[0] * 16 * cote, m[1] + nAB[1] * 16 * cote + 4], etiquettes[cle]);
    });
    if (etiquettes.AB) {
        // Étiquette de la longueur AB au MILIEU DE [IB] (jamais en I, où passe (d)),
        // du côté de (d) opposé au petit carré — loin de « I » (retour prof 2026-09-02).
        const m = milieu(pI, pB);
        const cote = dot(nAB, uID) > 0 ? -1 : 1;
        s += svgTexteLibre([m[0] + nAB[0] * 16 * cote, m[1] + nAB[1] * 16 * cote + 4], etiquettes.AB);
    }
    void nAB;
    return svgEnvelopper(s, fig.avecM ? { A, B, I, M } : { A, B, I });
};

let derniereSignaturePoint1078 = null;

const genPointSaisie = () => {
    let payload = construirePointSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignaturePoint1078; t++) payload = construirePointSaisie();
    derniereSignaturePoint1078 = payload._meta.params.signature;
    return payload;
};

const construirePointSaisie = () => {
    // Trois gestes de cycle 3 (revue 2026-09-03, A8) : recopier (MB = MA, dans
    // les deux sens), moitié d'un pair ≤ 24, double d'un entier ≤ 12.
    const sousType = randomChoice(['equidistance', 'moitie', 'double']);
    let texte; let reponse; let svg;
    if (sousType === 'equidistance') {
        const k = randInt(3, 15);
        const [donne, cherche] = shuffle(['MA', 'MB']);
        const fig = construireFigurePoint(true);
        svg = svgFigurePoint(fig, { [donne]: `${k} cm` });
        texte = `<p>La droite (d) est la médiatrice du segment [AB]. Le point M est sur (d) et ${donne} = ${k} cm.</p><p>Que vaut ${cherche} ?</p>`;
        reponse = k;
    } else if (sousType === 'moitie') {
        const n = randInt(2, 12);
        const fig = construireFigurePoint(false);
        svg = svgFigurePoint(fig, { AB: `${2 * n} cm` });
        const cible = randomChoice(['IA', 'IB']);
        texte = `<p>La droite (d) est la médiatrice du segment [AB] ; elle coupe [AB] en I. On a AB = ${2 * n} cm.</p><p>Que vaut ${cible} ?</p>`;
        reponse = n;
    } else {
        const n = randInt(2, 12);
        const fig = construireFigurePoint(false);
        const donne = randomChoice(['IA', 'IB']);
        svg = svgFigurePoint(fig, { [donne]: `${n} cm` });
        texte = `<p>La droite (d) est la médiatrice du segment [AB] ; elle coupe [AB] en I. On a ${donne} = ${n} cm.</p><p>Que vaut AB ?</p>`;
        reponse = 2 * n;
    }
    if (!Number.isInteger(reponse) || reponse <= 0) throw new Error('gen_1078 : réponse de saisie non entière');
    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_POINT), suffixe: 'cm', elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, reponse, signature: `${sousType}-${reponse}-${texte.slice(-14)}` };
    return payload;
};

// QCM justification : le sens direct (M sur (d) ⟹ MA = MB) et la réciproque.
const JUSTIFICATIONS = {
    // Gabarits homogènes (55-80 caractères, revue 2026-09-03, A2) : la bonne
    // réponse n'est plus repérable à sa longueur.
    direct: {
        texte: '<p>La droite (d) est la médiatrice de [AB] et M est un point de (d), en dehors de [AB].</p><p>Pourquoi peut-on affirmer que MA = MB ?</p>',
        bonne: 'M est sur la médiatrice de [AB], donc à égale distance de A et de B.',
        pieges: [
            '(d) est perpendiculaire à [AB] : M est donc à égale distance de A et de B.',   // insuffisant
            'M est le milieu de [AB] : il est donc à égale distance de A et de B.',         // faux
            'En mesurant MA et MB à la règle, on trouve la même longueur.',                 // pas une preuve
            'MA et MB ont l\'air de la même longueur sur le dessin.',                       // allure
        ],
    },
    reciproque: {
        texte: '<p>On sait que MA = MB, et M n\'est pas sur [AB].</p><p>Pourquoi peut-on affirmer que M est sur la médiatrice de [AB] ?</p>',
        bonne: 'M est à égale distance de A et de B, donc M est sur la médiatrice de [AB].',
        pieges: [
            'MA = MB : M est donc le milieu de [AB], et le milieu est sur la médiatrice.',   // faux
            'La médiatrice de [AB] passe par le milieu de [AB], donc par M.',                // faux
            'MA et MB ont l\'air de la même longueur sur le dessin.',                       // allure
            '(d) est perpendiculaire à [AB], donc tout point au-dessus de [AB] est sur (d).', // faux
        ],
    },
};

let derniereSignatureJustif1078 = null;

const genJustificationQcm = () => {
    let payload = construireJustificationQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureJustif1078; t++) payload = construireJustificationQcm();
    derniereSignatureJustif1078 = payload._meta.params.signature;
    return payload;
};

const construireJustificationQcm = () => {
    const sousType = randomChoice(['direct', 'reciproque']);
    const J = JUSTIFICATIONS[sousType];
    const pieges = shuffle(J.pieges).slice(0, 3);
    const fig = construireFigurePoint(true);
    const payload = makeQcm([J.bonne, ...pieges], J.bonne, J.texte, {
        ...opts(L_POINT), elements_additionnels: imgElement(svgFigurePoint(fig), 'medium'),
    });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType };
    payload._meta.params = { sousType, signature: `${sousType}-${pieges.map((p) => p.slice(0, 24)).sort().join('|')}` };
    return payload;
};

// ─── Carte 3 : le cercle circonscrit ───────────────────────────────────────

const NOMS_TRIANGLES = [['R', 'S', 'T'], ['D', 'E', 'F'], ['K', 'L', 'M'], ['P', 'Q', 'R'], ['U', 'V', 'W']];

/** Triangle (acutangle 75 % / obtusangle 25 %), centre O calculé. */
const construireFigureCercle = () => {
    const noms = randomChoice(NOMS_TRIANGLES);
    const obtus = Math.random() < 0.25;
    let A; let B; let C;
    for (let essai = 0; essai < 50; essai++) {
        A = [0, 0]; B = [6, 0];
        C = obtus ? [randomChoice([-1.6, -1.2, 7.2, 7.6]), randomChoice([2.2, 2.6])] : [randInt(18, 42) / 10, randInt(30, 44) / 10];
        const angles = [[A, B, C], [B, C, A], [C, A, B]].map(([P, Q, R]) => Math.acos(dot(unitaire(vec(P, Q)), unitaire(vec(P, R)))));
        const maxDeg = (Math.max(...angles) * 180) / Math.PI; const minDeg = (Math.min(...angles) * 180) / Math.PI;
        if (minDeg < 28) continue;
        if (obtus ? maxDeg > 100 && maxDeg < 125 : maxDeg < 82) break;
        C = null;
    }
    if (!C) throw new Error('gen_1078 : triangle introuvable');
    const O = circonscrit(A, B, C);
    const rot = Math.random() < 0.3 ? 0 : randInt(5, 355);
    const [rA, rB, rC, rO] = tourner([A, B, C, O], rot);
    const R = norme(vec(rO, rA));
    if (Math.abs(norme(vec(rO, rB)) - R) > 1e-6 || Math.abs(norme(vec(rO, rC)) - R) > 1e-6) throw new Error('gen_1078 : centre faux');
    return { noms, pts: [rA, rB, rC], O: rO, R, obtus, rot };
};

/** mediatrices : indices des côtés dont la médiatrice est tracée (0 = [AB], 1 = [BC], 2 = [CA]). */
const svgFigureCercle = (fig, avecCercle = true, mediatrices = [0, 1, 2]) => {
    const { pts, O, R, noms } = fig;
    const appuis = [...pts, [O[0] - R, O[1] - R], [O[0] + R, O[1] + R]];
    const c = cadreDe(appuis);
    const P = pts.map(c.P); const pO = c.P(O); const rpx = R * c.ech;
    let s = '';
    if (avecCercle) s += `<circle cx="${f1(pO[0])}" cy="${f1(pO[1])}" r="${f1(rpx)}" fill="none" stroke="#1D9E75" stroke-width="1.8"/>`;
    s += `<path d="M ${f1(P[0][0])} ${f1(P[0][1])} L ${f1(P[1][0])} ${f1(P[1][1])} L ${f1(P[2][0])} ${f1(P[2][1])} Z" fill="#eff6ff" fill-opacity="0.7" stroke="#1E3A8A" stroke-width="2.2"/>`;
    // Les trois médiatrices : de chaque milieu vers O, prolongées des deux côtés.
    const rayons = []; // directions (angles) des demi-droites issues de O, pour placer « O »
    for (let i = 0; i < 3; i++) {
        if (!mediatrices.includes(i)) continue;
        const U = P[i]; const V = P[(i + 1) % 3]; const m = milieu(U, V);
        const dir = unitaire(vec(m, pO)); const L1 = norme(vec(m, pO));
        const a = Math.atan2(dir[1], dir[0]); rayons.push(a, a + Math.PI);
        const ext = 18;
        const d1 = [m[0] - dir[0] * ext, m[1] - dir[1] * ext];
        const d2 = [pO[0] + dir[0] * (L1 < 1 ? 40 : ext + 16), pO[1] + dir[1] * (L1 < 1 ? 40 : ext + 16)];
        s += svgSegment(d1, d2, 'stroke="#B45309" stroke-width="1.5" stroke-dasharray="6 4"');
        s += svgTicks(U, m, i + 1, `${noms[i]}${noms[(i + 1) % 3]}a`) + svgTicks(m, V, i + 1, `${noms[i]}${noms[(i + 1) % 3]}b`);
        s += svgCarre(m, vec(m, V), vec(m, pO), `${noms[i]}${noms[(i + 1) % 3]}`);
    }
    const cx = (P[0][0] + P[1][0] + P[2][0]) / 3; const cy = (P[0][1] + P[1][1] + P[2][1]) / 3;
    P.forEach((p, i) => { s += svgPoint(p, noms[i], vec([cx, cy], p)); });
    // « O » sur la bissectrice du plus grand secteur libre entre les médiatrices
    // (revue 2026-09-03, A6) : jamais sur une droite passant par O.
    const angles = rayons.map((a) => ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)).sort((x, y) => x - y);
    let meilleur = -Math.PI / 4; let ecartMax = -1;
    if (angles.length) {
        for (let k = 0; k < angles.length; k++) {
            const suivant = k + 1 < angles.length ? angles[k + 1] : angles[0] + 2 * Math.PI;
            const ecart = suivant - angles[k];
            if (ecart > ecartMax) { ecartMax = ecart; meilleur = angles[k] + ecart / 2; }
        }
    }
    const lO = [pO[0] + Math.cos(meilleur) * 14, pO[1] + Math.sin(meilleur) * 14];
    s += `<circle cx="${f1(pO[0])}" cy="${f1(pO[1])}" r="2.8" fill="#1D9E75"/>`
        + `<text x="${f1(lO[0])}" y="${f1(lO[1] + 5)}" font-size="13" font-weight="700" fill="#1D9E75" text-anchor="middle">O</text>`;
    const points = { [noms[0]]: pts[0], [noms[1]]: pts[1], [noms[2]]: pts[2], O };
    return svgEnvelopper(s, points, ` data-rayon="${R.toFixed(6)}"`);
};

const OPTIONS_CENTRE = (n) => ({
    bonne: `le centre du cercle circonscrit au triangle ${n.join('')}`,
    pieges: [
        `le milieu du côté [${n[0]}${n[1]}]`,
        `le milieu du triangle ${n.join('')}`,   // réponse naïve (« hauteurs » retiré : vocabulaire de 5e, revue 2026-09-03)
        `un point à égale distance des trois côtés du triangle ${n.join('')}`,
    ],
});

let derniereSignatureCentre1078 = null;

const genCentreQcm = () => {
    let payload = construireCentreQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureCentre1078; t++) payload = construireCentreQcm();
    derniereSignatureCentre1078 = payload._meta.params.signature;
    return payload;
};

const construireCentreQcm = () => {
    const fig = construireFigureCercle();
    const n = fig.noms;
    const O = OPTIONS_CENTRE(n);
    const texte = `<p>On a tracé les trois médiatrices du triangle ${n.join('')} ; elles se coupent en O.</p><p>Que représente le point O pour le triangle ${n.join('')} ?</p>`;
    const payload = makeQcm([O.bonne, ...O.pieges], O.bonne, texte, {
        ...opts(L_CERCLE), elements_additionnels: imgElement(svgFigureCercle(fig), 'medium'),
    });
    payload._meta.params = { noms: n.join(''), obtus: fig.obtus, signature: `${n.join('')}-${fig.obtus}-${fig.rot}` };
    return payload;
};

let derniereSignatureRayon1078 = null;

const genRayonSaisie = () => {
    let payload = construireRayonSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureRayon1078; t++) payload = construireRayonSaisie();
    derniereSignatureRayon1078 = payload._meta.params.signature;
    return payload;
};

const construireRayonSaisie = () => {
    const fig = construireFigureCercle();
    const n = fig.noms;
    // Trois gestes de cycle 3 (revue 2026-09-03, A8) : recopier (rayon → rayon),
    // moitié (diamètre → rayon), double (rayon → diamètre).
    const sousType = randomChoice(['rayon', 'diametre', 'double']);
    const k = randInt(2, 9);
    const [donne, cherche] = shuffle([...n]).slice(0, 2);
    let texte; let reponse = k;
    if (sousType === 'rayon') {
        texte = `<p>O est le centre du cercle circonscrit au triangle ${n.join('')}. On a O${donne} = ${k} cm.</p><p>Que vaut O${cherche} ?</p>`;
    } else if (sousType === 'diametre') {
        texte = `<p>O est le centre du cercle circonscrit au triangle ${n.join('')}. Ce cercle a un diamètre de ${2 * k} cm.</p><p>Que vaut O${cherche} ?</p>`;
    } else {
        texte = `<p>O est le centre du cercle circonscrit au triangle ${n.join('')}. On a O${donne} = ${k} cm.</p><p>Quel est le diamètre de ce cercle ?</p>`;
        reponse = 2 * k;
    }
    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_CERCLE), suffixe: 'cm', elements_additionnels: imgElement(svgFigureCercle(fig), 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, k, signature: `${sousType}-${k}-${cherche}` };
    return payload;
};

// Texte à trous : définition, centre, et le POURQUOI (question d. de la fiche).
// Les lettres du triangle suivent la figure (n = [R, S, T] par exemple).
const PHRASES_CERCLE = [
    {
        phrase: (n) => `Le cercle circonscrit au triangle ${n.join('')} est l'unique cercle qui passe par ses trois {{sommets}}.`,
        distracteurs: ['côtés', 'milieux', 'médiatrices'],
    },
    {
        phrase: (n) => `Le centre du cercle circonscrit au triangle ${n.join('')} est le point de concours de ses trois {{médiatrices}}.`,
        distracteurs: ['sommets', 'côtés', 'diagonales'],   // « hauteurs » retiré : vocabulaire de 5e (revue 2026-09-03, A3)
    },
    {
        phrase: () => 'Pour obtenir le centre du cercle circonscrit, il suffit de tracer {{deux}} médiatrices : la {{troisième}} passe forcément par leur point d\'intersection.',
        distracteurs: ['trois', 'une', 'quatre'],   // « hauteur » retiré : vocabulaire de 5e (2026-09-03)
        mediatrices: [0, 1],
    },
    {
        phrase: (n) => `O est sur la médiatrice de [${n[0]}${n[1]}], donc O${n[0]} = {{O${n[1]}}}.`,
        // Jamais « O + troisième sommet » : OU = OW serait VRAI (revue 2026-09-03, A1).
        distracteurs: (n) => [`${n[0]}${n[1]}`, `${n[1]}${n[2]}`, `${n[0]}${n[2]}`],
        mediatrices: [0],
    },
    {
        phrase: (n) => `O${n[0]} = O${n[1]} et O${n[1]} = O${n[2]}, donc O est à égale distance des trois {{sommets}} : O${n[0]}, O${n[1]} et O${n[2]} sont trois {{rayons}} du cercle.`,
        distracteurs: ['côtés', 'diamètres', 'milieux'],
        mediatrices: [0, 1],
    },
    {
        phrase: (n) => `Comme O est à égale distance de ${n[0]}, ${n[1]} et ${n[2]}, il est aussi sur la {{médiatrice}} de [${n[0]}${n[2]}] : la tracer est {{inutile}}.`,
        distracteurs: ['droite', 'parallèle', 'obligatoire'],   // « hauteur » retiré : vocabulaire de 5e (2026-09-03)
        mediatrices: [0, 1],
    },
    {
        phrase: () => 'Le rayon du cercle circonscrit est la distance du {{centre}} O à l\'un des {{sommets}} du triangle.',
        distracteurs: ['milieu', 'côtés', 'cercle'],
    },
];

let dernierePhraseCercle1078 = -1;

const genCercleTrou = () => {
    let k = randInt(0, PHRASES_CERCLE.length - 1);
    if (k === dernierePhraseCercle1078) k = (k + 1) % PHRASES_CERCLE.length;
    dernierePhraseCercle1078 = k;
    const fig = construireFigureCercle();
    const n = fig.noms;
    const P = PHRASES_CERCLE[k];
    const phrase = P.phrase(n);
    const distracteurs = typeof P.distracteurs === 'function' ? P.distracteurs(n) : P.distracteurs;
    const attendus = [...phrase.matchAll(/\{\{([^}]+)\}\}/g)].map((m) => m[1]);
    if (attendus.length < 1 || attendus.length > 2) throw new Error('gen_1078 : phrase cercle sans trou');
    if (distracteurs.some((d) => attendus.includes(d))) throw new Error('gen_1078 : distracteur cercle égal à un mot attendu');
    const payload = makeTexteTrou(phrase, `<p>O est le centre du cercle circonscrit au triangle ${n.join('')}. Complète la phrase avec les mots proposés.</p>`, {
        ...opts(L_CERCLE), mode: 'drag', distracteurs: shuffle(distracteurs), strict: false,
        // La figure ne montre que les médiatrices dont la phrase parle : celle de
        // [RT] n'est PAS tracée quand la phrase dit qu'elle est inutile (retour prof 2026-09-03).
        elements_additionnels: imgElement(svgFigureCercle(fig, true, P.mediatrices || [0, 1, 2]), 'medium'),
    });
    payload._meta.params = { phrase: k, noms: n.join(''), mediatrices: (P.mediatrices || [0, 1, 2]).join('') };
    return payload;
};

// ─── V/F : moteur commun (2 lignes fixes + 2 lignes numériques recalculées) ─
// Même recette que le gen 1077 : deux lignes NUMÉRIQUES (une par sous-type) et
// deux lignes fixes ; fenêtre stricte des 4 dernières lignes fixes servies (soit
// DEUX tirages complets) ; chaque ligne numérique exclut la ligne fixe dont elle
// se déduit (A8). Mixité 1 à 3 faux sur 4.

/** incompatibles : paires de lignes fixes jamais servies ensemble (A8, négation l'une de l'autre). */
const genVfDepuis = (pool, fenetre, faireNumerique, label, incompatibles = []) => {
    for (let essai = 0; essai < 60; essai++) {
        // La ligne numérique est RETIRÉE à chaque essai : sa vérité participe à la mixité.
        const { numeriques, exclues } = faireNumerique();
        const dispo = pool.filter((it) => !fenetre.liste.includes(it.l) && !exclues.includes(it.l));
        const premiere = randomChoice(dispo);
        const interdites = incompatibles.filter((paire) => paire.includes(premiere.l)).flat();
        const seconde = randomChoice(dispo.filter((it) => it !== premiere && !interdites.includes(it.l)));
        const fixes = [premiere, seconde];
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        fenetre.liste = [...fenetre.liste, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(label),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1078 : V/F sans mixité après 60 essais');
};

// ─── Carte 1c : vrai ou faux ? — la médiatrice (rattaché à « Est-ce la médiatrice ? »)

const POOL_VF_MEDIATRICE = [
    // Les lignes « passe par le milieu » et « tout point est à égale distance » ne sont
    // PAS dans le pool : les deux lignes numériques les testent déjà (A8, 2026-09-03).
    { l: 'La médiatrice de [AB] est perpendiculaire à [AB].', v: true },
    { l: 'Un point à égale distance de A et de B est sur la médiatrice de [AB].', v: true },
    { l: 'Si M n\'est pas à égale distance de A et de B, alors M n\'est pas sur la médiatrice de [AB].', v: true },
    { l: 'La médiatrice de [AB] est l\'axe de symétrie du segment [AB].', v: true },
    { l: 'La médiatrice de [AB] est parallèle à [AB].', v: false },
    { l: 'La médiatrice d\'un segment est un segment.', v: false },
    { l: 'Toute droite passant par le milieu de [AB] est la médiatrice de [AB].', v: false },
    { l: 'Un segment a deux médiatrices.', v: false },
    { l: 'La médiatrice de [AB] passe par A.', v: false },
    { l: 'Tous les points à égale distance de A et de B forment la médiatrice de [AB].', v: true },
];
if (POOL_VF_MEDIATRICE.filter((it) => it.v).length !== 5) throw new Error('gen_1078 : pool V/F médiatrice déséquilibré');

const fenetreVfMediatrice1078 = { liste: [] };

const numeriqueMediatrice = () => {
    // Ligne NUMÉRIQUE recalculée : équidistance ou moitié, vraie ou fausse.
    const k = randInt(3, 12); const n = randInt(2, 9);
    const faux = Math.random() < 0.5;
    const autre = faux ? k + randomChoice([-2, -1, 1, 2]) : k;
    const faux2 = Math.random() < 0.5;
    const numeriques = [
        { l: `Le point M est sur la médiatrice de [AB] et MA = ${k} cm : alors MB = ${autre} cm.`, v: autre === k },
        { l: `La médiatrice de [AB] coupe [AB] en I et AB = ${2 * n} cm : alors IA = ${faux2 ? 2 * n : n} cm.`, v: !faux2 },
    ];
    return { numeriques, exclues: [] };
};

const genVfMediatrice = () => genVfDepuis(POOL_VF_MEDIATRICE, fenetreVfMediatrice1078, numeriqueMediatrice, L_LECTURE);

// ─── Carte 3d : vrai ou faux ? — le cercle circonscrit (déplacé ici, décision prof 2026-09-03)

const POOL_VF_CERCLE = [
    // « centre à égale distance des trois sommets » et « OR, OS, OT sont des rayons » ne
    // sont PAS dans le pool : les deux lignes numériques les testent déjà (A8, 2026-09-03).
    { l: 'Les trois médiatrices d\'un triangle se coupent en un même point.', v: true },
    { l: 'Le cercle circonscrit à un triangle passe par ses trois sommets.', v: true },
    { l: 'Deux médiatrices suffisent pour trouver le centre du cercle circonscrit.', v: true },
    { l: 'Le rayon du cercle circonscrit est la distance de son centre à un sommet du triangle.', v: true },
    { l: 'Le centre du cercle circonscrit à un triangle est sur la médiatrice de chacun de ses côtés.', v: true },
    { l: 'Il faut tracer les trois médiatrices pour trouver le centre du cercle circonscrit.', v: false },
    { l: 'Le centre du cercle circonscrit est toujours à l\'intérieur du triangle.', v: false },
    { l: 'Le centre du cercle circonscrit à un triangle est à égale distance des trois côtés.', v: false },
    { l: 'Le cercle circonscrit à un triangle passe par le milieu de chaque côté.', v: false },
    { l: 'Le centre du cercle circonscrit est toujours le milieu d\'un côté du triangle.', v: false },
];
if (POOL_VF_CERCLE.filter((it) => it.v).length !== 5) throw new Error('gen_1078 : pool V/F cercle déséquilibré');

const fenetreVfCercle1078 = { liste: [] };

const numeriqueCercle = () => {
    // Ligne NUMÉRIQUE recalculée : rayon connu ou diamètre connu, vraie ou fausse.
    const n = randomChoice(NOMS_TRIANGLES);
    const k = randInt(2, 9);
    const faux = Math.random() < 0.5;
    const [donne, cherche] = shuffle([...n]).slice(0, 2);
    const faux2 = Math.random() < 0.5;
    const n2 = randomChoice(NOMS_TRIANGLES.filter((x) => x !== n)); const k2 = randInt(2, 9);
    const numeriques = [
        { l: `O est le centre du cercle circonscrit au triangle ${n.join('')} et O${donne} = ${k} cm : alors O${cherche} = ${faux ? k + randomChoice([-1, 1, 2]) : k} cm.`, v: !faux },
        { l: `Le cercle circonscrit au triangle ${n2.join('')}, de centre O, a un diamètre de ${2 * k2} cm : alors O${n2[randInt(0, 2)]} = ${faux2 ? 2 * k2 : k2} cm.`, v: !faux2 },
    ];
    return { numeriques, exclues: [] };
};

const INCOMPATIBLES_CERCLE = [[
    'Deux médiatrices suffisent pour trouver le centre du cercle circonscrit.',
    'Il faut tracer les trois médiatrices pour trouver le centre du cercle circonscrit.',
]]; // négation l'une de l'autre (revue 2026-09-03, A8)

const genVfCercle = () => genVfDepuis(POOL_VF_CERCLE, fenetreVfCercle1078, numeriqueCercle, L_CERCLE, INCOMPATIBLES_CERCLE);

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    lecture_qcm_unique: genLectureQcm,
    lecture_texte_trou: genDefinitionTrou,
    point_saisie: genPointSaisie,
    point_justification_qcm_unique: genJustificationQcm,
    cercle_centre_qcm_unique: genCentreQcm,
    cercle_rayon_saisie: genRayonSaisie,
    cercle_texte_trou: genCercleTrou,
    lecture_tableau_vf: genVfMediatrice,
    cercle_tableau_vf: genVfCercle,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou: 'Texte à trous', saisie: 'Saisie', tableau_vf: 'Vrai/Faux',
    justification_qcm_unique: 'QCM justification',
    centre_qcm_unique: 'QCM centre', rayon_saisie: 'Saisie rayon',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lecture', label: L_LECTURE, formatsList: buildFormats('lecture') },
    { id: 'point', label: L_POINT, formatsList: buildFormats('point') },
    { id: 'cercle', label: L_CERCLE, formatsList: buildFormats('cercle') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
